import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabase";
import { searchPrints } from "../../utility/printsApi";
import {
  artworkImageSrcSet,
  artworkImageUrl,
} from "../../utility/artworkImage";
import {
  PRIMARY_TRAIT_COLLECTION_TYPE,
  withoutRetiredTraits,
} from "../../constants/traits";
import { collectCovers, coverKey } from "../../utility/collectionCovers";
import Button from "../Button";
import DeferredImage from "../DeferredImage";

const TABS = [
  {
    id: "recommended_room",
    label: "Room",
    slug: "recommended-room",
    blurb: "Find art sized and toned for the space it's going in.",
  },
  {
    id: "color_psychology_primary_trait",
    label: "Mood",
    slug: "color-psychology-primary-trait",
    blurb: "Pick art by the feeling its colour palette creates.",
  },
];

// Mirrors the link construction in pages/Collections/Collections.js
const valueSlug = (value) =>
  String(value).toLowerCase().replace(/\s+/g, "-");

const ShopBy = () => {
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [collections, setCollections] = useState([]);
  const [covers, setCovers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (window.__CREATE_PRINTS_PRERENDER__) return undefined;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [collectionsResult, printsResult] = await Promise.all([
          supabase
            .from("collection_metadata")
            .select("id, collection_type, value, title")
            .in(
              "collection_type",
              TABS.map((tab) => tab.id)
            )
            .neq("value", "Undefined")
            .neq("value", "undefined"),
          // Covers come from the catalogue API rather than a 300-row Supabase
          // read on the homepage: it is edge-cached, it already applies the
          // moderation gate, and it returns the three fields this needs
          // instead of whole rows. A page of 96 recent prints covers every
          // room and every mood several times over.
          searchPrints({ limit: 96 }),
        ]);

        if (cancelled) return;

        if (collectionsResult.error) throw collectionsResult.error;

        setCollections(withoutRetiredTraits(collectionsResult.data));

        // Derive one representative cover image per collection value from a
        // single batch of recent prints, rather than a request per category.
        // The two tabs are collected separately so a room claim never leaves a
        // mood tile without a cover.
        const prints = printsResult.prints || [];
        setCovers({
          ...collectCovers(prints, (print) => {
            const rooms = Array.isArray(print.recommended_room)
              ? print.recommended_room
              : [print.recommended_room];
            return rooms
              .filter(Boolean)
              .map((room) => coverKey("recommended_room", room));
          }),
          ...collectCovers(prints, (print) =>
            print.color_psychology_primary_trait
              ? [
                  coverKey(
                    PRIMARY_TRAIT_COLLECTION_TYPE,
                    print.color_psychology_primary_trait
                  ),
                ]
              : []
          ),
        });
        setError(false);
      } catch (err) {
        if (!cancelled) setError(true);
        console.error("Error loading shop-by categories:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeTabConfig = TABS.find((tab) => tab.id === activeTab);

  const visibleCollections = useMemo(
    () =>
      collections
        .filter((collection) => collection.collection_type === activeTab)
        .sort((a, b) => String(a.value).localeCompare(String(b.value))),
    [collections, activeTab]
  );

  return (
    <section className="py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
              Shop by
            </h2>
            <p className="mt-2 text-base text-slate-600">
              {activeTabConfig?.blurb}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div
              role="tablist"
              aria-label="Shop by category"
              className="flex gap-2 rounded-full bg-white p-1"
            >
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  type="button"
                  aria-selected={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`min-h-[2.75rem] rounded-full px-4 py-2 text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <Button
              url={`/collections/${activeTabConfig?.slug}`}
              variant="secondary"
              size="sm"
            >
              View all
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-8 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {loading &&
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={`shopby-skeleton-${index}`}
              className="w-36 flex-none snap-start sm:w-44"
            >
              <div className="h-36 w-full animate-pulse rounded-xl bg-slate-200 sm:h-44" />
              <div className="mx-auto mt-3 h-4 w-2/3 animate-pulse rounded bg-slate-200" />
            </div>
          ))}

        {!loading &&
          visibleCollections.map((collection) => {
            const cover =
              covers[coverKey(collection.collection_type, collection.value)];

            return (
              <Link
                key={collection.id}
                to={`/collections/${activeTabConfig.slug}/${valueSlug(
                  collection.value
                )}`}
                className="group w-36 flex-none snap-start sm:w-44"
              >
                <div className="h-36 overflow-hidden rounded-xl bg-white sm:h-44">
                  {cover ? (
                    <DeferredImage
                      src={artworkImageUrl(cover, 320)}
                      srcSet={artworkImageSrcSet(cover, [256, 320, 384])}
                      sizes="(min-width: 640px) 176px, 144px"
                      fallbackSrc={cover}
                      alt={`${collection.value} wall art prints`}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-slate-100 px-3 text-center font-serif text-lg font-bold text-slate-400">
                      {collection.value}
                    </div>
                  )}
                </div>
                <p className="mt-3 text-center text-sm font-semibold text-slate-950">
                  {collection.value}
                </p>
              </Link>
            );
          })}
      </div>

      {!loading && error && (
        <div className="mx-auto max-w-7xl px-6">
          <p className="text-sm text-slate-600">
            We couldn't load these categories right now.{" "}
            <Link to="/collections" className="font-semibold underline">
              Browse all collections
            </Link>
          </p>
        </div>
      )}

      {!loading && !error && visibleCollections.length === 0 && (
        <div className="mx-auto max-w-7xl px-6">
          <p className="text-sm text-slate-600">
            No categories to show yet.
          </p>
        </div>
      )}
    </section>
  );
};

export default ShopBy;
